import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check, ListChecks, Search, X } from 'lucide-react';
import Button from '../ui/Button';
import { Tabs } from '../ui/Tabs';
import inductionService from '../../services/induction';
import { errorMessage } from '../../utils/apiResponse';
import { formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import { cn } from '../../utils/cn';
import type { InductionPerson, InductionRegister, RegisterCell } from '../../types/induction';
import BulkSignOffDialog, { type BulkEntry } from './BulkSignOffDialog';
import PackMatrix from './PackMatrix';
import { BASIS_HINTS, WORKER_TYPES, personLabel } from './inductionLabels';

interface Props {
  canAssess: boolean;
  canManagePack: boolean;
  /** Enforcement setting, when known. */
  enforce: boolean | null;
}

type Filter = 'all' | 'missing';
type View = 'equipment' | 'pack';
type RegisterInduction = InductionRegister['inductions'][number];

const selKey = (ref: string, inductionId: number) => `${ref}|${inductionId}`;

function cellFor(reg: InductionRegister, p: InductionPerson, inductionId: number): RegisterCell | undefined {
  return p.worker_id ? reg.cells[`w${p.worker_id}:${inductionId}`] : undefined;
}

/** A cell that can be signed off now: nothing on record, or the record has lapsed. */
function selectable(cell: RegisterCell | undefined) {
  return !cell || cell.status === 'expired';
}

/**
 * The register: every person against every equipment induction. Bulk mode is
 * the roll-out tool — tick whole rows or whole columns ("everyone on the
 * tractor") and record them in one go, so turning enforcement on does not
 * strand a farm's existing operators.
 */
export default function RegisterPanel({ canAssess, canManagePack, enforce }: Props) {
  const tz = useOrgTimezone();
  const [reg, setReg] = useState<InductionRegister | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [view, setView] = useState<View>('equipment');
  const [bulk, setBulk] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReg(await inductionService.register());
    } catch (e) {
      setError(errorMessage(e, 'Could not load the register'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const inductions: RegisterInduction[] = useMemo(() => reg?.inductions ?? [], [reg]);
  const required = useMemo(() => {
    const r = inductions.filter(i => i.is_required);
    return r.length ? r : inductions;
  }, [inductions]);

  const people = useMemo(() => {
    if (!reg) return [];
    const q = search.trim().toLowerCase();
    return reg.people.filter(p => {
      if (q && ![p.name, p.picker_code, p.labour_provider_name].some(v => (v || '').toLowerCase().includes(q))) return false;
      if (filter === 'missing') {
        return required.some(i => {
          const c = cellFor(reg, p, i.id);
          return !c || c.status === 'expired';
        });
      }
      return true;
    });
  }, [reg, search, filter, required]);

  const withNothing = useMemo(() => {
    if (!reg) return 0;
    return reg.people.filter(p => !inductions.some(i => {
      const c = cellFor(reg, p, i.id);
      return c && c.status !== 'expired';
    })).length;
  }, [reg, inductions]);

  const toggle = (keys: string[]) => {
    setSelected(prev => {
      const next = new Set(prev);
      const allOn = keys.length > 0 && keys.every(k => next.has(k));
      for (const k of keys) {
        if (allOn) next.delete(k); else next.add(k);
      }
      return next;
    });
  };

  const rowKeys = (p: InductionPerson) =>
    reg ? inductions.filter(i => selectable(cellFor(reg, p, i.id))).map(i => selKey(p.ref, i.id)) : [];
  const colKeys = (i: RegisterInduction) =>
    reg ? people.filter(p => selectable(cellFor(reg, p, i.id))).map(p => selKey(p.ref, i.id)) : [];

  const entries: BulkEntry[] = useMemo(() => {
    if (!reg) return [];
    const byRef = new Map<string, BulkEntry>();
    for (const p of reg.people) {
      for (const i of inductions) {
        if (!selected.has(selKey(p.ref, i.id))) continue;
        if (!byRef.has(p.ref)) byRef.set(p.ref, { ref: p.ref, name: p.name, inductions: [] });
        byRef.get(p.ref)!.inductions.push({ id: i.id, title: i.title });
      }
    }
    return [...byRef.values()];
  }, [reg, inductions, selected]);

  const exitBulk = () => { setBulk(false); setSelected(new Set()); };

  const showPackToggle = canManagePack && !!reg && reg.pack.length > 0;

  if (loading && !reg) return <p className="text-sm text-neutral-500">Loading the register…</p>;
  if (error && !reg) return <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>;
  if (!reg) return null;

  const cellView = (p: InductionPerson, i: RegisterInduction) => {
    const cell = cellFor(reg, p, i.id);
    if (bulk && selectable(cell)) {
      const k = selKey(p.ref, i.id);
      return (
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            className="h-4 w-4 shrink-0"
            checked={selected.has(k)}
            onChange={() => toggle([k])}
            aria-label={`${personLabel(p.name)} — ${i.title}`}
          />
          {cell?.status === 'expired' && <span className="text-xs text-error-700">Expired</span>}
        </label>
      );
    }
    return <CellView cell={cell} tz={tz} />;
  };

  return (
    <div className="space-y-4">
      {enforce && withNothing >= 3 && (
        <div className="rounded-lg border border-warning-200 bg-warning-50 p-3 sm:p-4 text-sm">
          <div className="flex items-start gap-2 min-w-0">
            <AlertTriangle className="h-5 w-5 text-warning-600 shrink-0" />
            <div className="min-w-0 break-words">
              <p className="font-semibold text-warning-900">Record your existing operators so their jobs are not held up</p>
              <p className="text-warning-800 mt-1">
                {withNothing} people have no equipment sign-offs yet. Enforcement is on, so they cannot be put on a
                machine or pre-start one until they are signed off.
              </p>
              {canAssess && !bulk && (
                <Button size="sm" variant="secondary" className="mt-2" onClick={() => { setBulk(true); setFilter('missing'); }}>
                  <ListChecks className="h-4 w-4" /> Start bulk sign-off
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {message && (
        <div className="rounded-lg border border-success-200 bg-success-50 p-3 text-sm text-success-800 flex items-start justify-between gap-2">
          <span className="min-w-0 break-words">{message}</span>
          <button type="button" onClick={() => setMessage(null)} aria-label="Dismiss" className="shrink-0 text-success-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {showPackToggle && (
        <Tabs
          tabs={[{ key: 'equipment', label: 'Equipment' }, { key: 'pack', label: 'Policies acknowledged' }]}
          active={view}
          onChange={k => { setView(k); exitBulk(); }}
        />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 basis-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <input
            type="search"
            className="w-full border border-neutral-300 rounded-lg pl-9 pr-3 py-2 text-sm bg-white"
            placeholder="Search people"
            aria-label="Search people"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {view === 'equipment' && (
          <select
            className="border border-neutral-300 rounded-lg px-3 py-2 text-sm bg-white max-w-full"
            value={filter}
            onChange={e => setFilter(e.target.value as Filter)}
            aria-label="Show"
          >
            <option value="all">Everyone</option>
            <option value="missing">Missing a required induction</option>
          </select>
        )}
        {view === 'equipment' && canAssess && inductions.length > 0 && (
          bulk
            ? <Button size="sm" variant="ghost" onClick={exitBulk}><X className="h-4 w-4" /> Cancel bulk</Button>
            : <Button size="sm" variant="secondary" onClick={() => setBulk(true)}><ListChecks className="h-4 w-4" /> Bulk sign-off</Button>
        )}
      </div>

      {view === 'pack' ? (
        <PackMatrix register={reg} people={people} tz={tz} />
      ) : inductions.length === 0 ? (
        <p className="text-sm text-neutral-500 break-words">
          No equipment inductions are set up yet. Set them up on the Equipment tab — one per type of machine you own.
        </p>
      ) : (
        <>
          {bulk && (
            <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 p-3 text-sm">
              <span className="min-w-0 flex-1 basis-40 break-words text-primary-900">
                {selected.size === 0
                  ? 'Tick people, whole rows, or a whole column (“Everyone”).'
                  : `${selected.size} sign-off${selected.size === 1 ? '' : 's'} for ${entries.length} ${entries.length === 1 ? 'person' : 'people'}`}
              </span>
              {selected.size > 0 && (
                <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
              )}
              <Button size="sm" disabled={selected.size === 0} onClick={() => setConfirming(true)}>Record…</Button>
            </div>
          )}

          {bulk && (
            <div className="md:hidden flex flex-wrap gap-2">
              {inductions.map(i => {
                const keys = colKeys(i);
                return (
                  <button
                    key={i.id}
                    type="button"
                    disabled={keys.length === 0}
                    onClick={() => toggle(keys)}
                    className={cn(
                      'max-w-full rounded-full border px-3 py-1 text-xs text-left break-words disabled:opacity-40',
                      keys.length > 0 && keys.every(k => selected.has(k))
                        ? 'border-primary-600 bg-primary-600 text-white'
                        : 'border-neutral-300 bg-white text-neutral-700',
                    )}
                  >
                    Everyone on {i.title}
                  </button>
                );
              })}
            </div>
          )}

          {people.length === 0 && <p className="text-sm text-neutral-500">Nobody matches.</p>}

          {/* Phones: one card per person. */}
          <ul className="md:hidden space-y-3">
            {people.map(p => {
              const keys = rowKeys(p);
              return (
                <li key={p.ref} className="rounded-lg border border-neutral-200 p-3">
                  <div className="flex items-start justify-between gap-2 min-w-0">
                    <PersonName person={p} />
                    {bulk && keys.length > 0 && (
                      <button type="button" className="shrink-0 text-xs font-medium text-primary-700" onClick={() => toggle(keys)}>
                        {keys.every(k => selected.has(k)) ? 'None' : 'All'}
                      </button>
                    )}
                  </div>
                  <ul className="mt-2 divide-y divide-neutral-100">
                    {inductions.map(i => (
                      <li key={i.id} className="flex items-center justify-between gap-3 py-1.5 text-sm">
                        <span className="min-w-0 break-words text-neutral-700">{i.title}</span>
                        <span className="shrink-0 text-right">{cellView(p, i)}</span>
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>

          {/* Wider screens: the matrix, which scrolls sideways when there are many machines. */}
          {people.length > 0 && (
            <div className="hidden md:block overflow-x-auto rounded-lg border border-neutral-200">
              <table className="min-w-full text-sm">
                <thead className="bg-neutral-50 text-left text-xs font-medium text-neutral-600">
                  <tr>
                    <th className="px-3 py-2 min-w-[12rem]">Person</th>
                    {inductions.map(i => {
                      const keys = colKeys(i);
                      return (
                        <th key={i.id} className="px-3 py-2 min-w-[7rem] max-w-[11rem] align-bottom">
                          <Link to={`/inductions/equipment/${i.id}`} className="break-words hover:underline">{i.title}</Link>
                          {!i.is_required && <span className="block font-normal text-neutral-400">optional</span>}
                          {bulk && (
                            <label className="mt-1 flex items-center gap-1 font-normal text-primary-700">
                              <input
                                type="checkbox"
                                className="h-4 w-4"
                                disabled={keys.length === 0}
                                checked={keys.length > 0 && keys.every(k => selected.has(k))}
                                onChange={() => toggle(keys)}
                                aria-label={`Everyone on ${i.title}`}
                              />
                              Everyone
                            </label>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {people.map(p => {
                    const keys = rowKeys(p);
                    return (
                      <tr key={p.ref} className="align-top">
                        <td className="px-3 py-2 max-w-[16rem]">
                          <div className="flex items-start gap-2 min-w-0">
                            {bulk && (
                              <input
                                type="checkbox"
                                className="mt-0.5 h-4 w-4 shrink-0"
                                disabled={keys.length === 0}
                                checked={keys.length > 0 && keys.every(k => selected.has(k))}
                                onChange={() => toggle(keys)}
                                aria-label={`All of ${personLabel(p.name)}`}
                              />
                            )}
                            <PersonName person={p} />
                          </div>
                        </td>
                        {inductions.map(i => <td key={i.id} className="px-3 py-2">{cellView(p, i)}</td>)}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {confirming && (
        <BulkSignOffDialog
          entries={entries}
          onClose={() => setConfirming(false)}
          onDone={r => {
            setConfirming(false);
            exitBulk();
            setMessage(`Recorded ${r.records} sign-off${r.records === 1 ? '' : 's'} for ${r.people} ${r.people === 1 ? 'person' : 'people'}.`);
            load();
          }}
        />
      )}
    </div>
  );
}

function PersonName({ person }: { person: InductionPerson }) {
  const meta = [WORKER_TYPES[person.worker_type] ?? person.worker_type, person.labour_provider_name, person.picker_code]
    .filter(Boolean).join(' · ');
  return (
    <div className="min-w-0">
      <Link to={`/inductions/people/${person.ref}`} className="font-medium text-primary-700 hover:underline break-words">
        {personLabel(person.name)}
      </Link>
      <div className="text-xs text-neutral-500 break-words">
        {meta}{!person.has_login && ' · no login'}
      </div>
    </div>
  );
}

function CellView({ cell, tz }: { cell: RegisterCell | undefined; tz: string }) {
  if (!cell) return <span className="text-neutral-300" aria-label="Not signed off">—</span>;
  const hint = BASIS_HINTS[cell.basis];
  const date = cell.expires_on ? formatLocalDateInTz(cell.expires_on, tz, { day: 'numeric', month: 'short', year: '2-digit' }) : null;
  return (
    <span className="inline-flex flex-col items-end md:items-start text-xs leading-tight">
      {cell.status === 'current' && (
        <span className="inline-flex items-center gap-1 text-success-700 font-medium"><Check className="h-4 w-4" />Current</span>
      )}
      {cell.status === 'expiring' && <span className="text-warning-800 font-medium whitespace-nowrap">Expires {date}</span>}
      {cell.status === 'expired' && <span className="text-error-700 font-medium whitespace-nowrap">Expired {date}</span>}
      {cell.status === 'current' && date && <span className="text-neutral-500 whitespace-nowrap">to {date}</span>}
      {hint && <span className="text-neutral-400">{hint}</span>}
    </span>
  );
}
